import { Paperclip } from 'lucide-react';
import { useMemo, useState, type JSX } from 'react';
import { useIntl } from 'react-intl';

import type { ApprovalRequestInput } from '../projects/components/ApprovalRequestBlock.display';

import {
  MailComposeApprovalLayout,
  type MailComposeValues,
} from './mail-compose-approval-layout';
import { parseAddressListInput } from './nango-connector-approval';
import {
  useNangoApprovalSubmit,
  type NangoConnectorApprovalResolve,
} from './nango-connector-approval-card';
import {
  buildSessionEmailModifiedInput,
  readSessionEmailApprovalFields,
} from './session-email-approval';

export interface SessionEmailApprovalCardProps {
  approval: ApprovalRequestInput;
  onResolve: NangoConnectorApprovalResolve;
}

/**
 * `email_session_content` (Heidi Share email) approval, laid out like the
 * connected-mailbox send card so both email paths review the same way.
 */
export function SessionEmailApprovalCard({
  approval,
  onResolve,
}: SessionEmailApprovalCardProps): JSX.Element {
  const intl = useIntl();
  const { pendingVerdict, isBusy, submit } = useNangoApprovalSubmit(
    approval.approval_id,
    onResolve
  );
  const fields = useMemo(() => readSessionEmailApprovalFields(approval), [approval]);
  const [values, setValues] = useState<MailComposeValues>(() => fields.initial);

  const title =
    fields.kind === 'document'
      ? intl.formatMessage({ defaultMessage: 'Email document', id: 'qArw7+QV9s' })
      : intl.formatMessage({ defaultMessage: 'Email consult note', id: 'yjTZTkalpG' });

  const bodyPlaceholder = fields.preview?.hasAttachment
    ? intl.formatMessage({
        defaultMessage: 'Leave blank to attach the completed PDF form',
        id: 'vWm2Be90QK',
        description: 'Session email approval: body hint when the document is a PDF form',
      })
    : fields.kind === 'document'
      ? intl.formatMessage({
          defaultMessage: 'Leave blank to send the saved document',
          id: '2DVrHxKZmG',
          description: 'Session email approval: body hint when no preview is available',
        })
      : intl.formatMessage({
          defaultMessage: 'Leave blank to send the saved consult note',
          id: 'f2ahAiFOy3',
          description: 'Session email approval: body hint when no preview is available',
        });

  return (
    <MailComposeApprovalLayout
      testId={`session-email-approval-${approval.approval_id}`}
      fieldTestIdPrefix="session-email-approval"
      title={title}
      values={values}
      onChange={(field, value) => setValues((prev) => ({ ...prev, [field]: value }))}
      bodyPlaceholder={bodyPlaceholder}
      footnote={
        fields.preview?.hasAttachment && !values.body.trim() ? (
          <div className="flex items-center gap-2 px-3 pb-2 text-xs text-text-secondary">
            <Paperclip className="size-3.5 shrink-0" />
            <span data-testid="session-email-approval-attachment">
              {intl.formatMessage({
                defaultMessage: 'Completed PDF form attached',
                id: 'pAK8lLHsZm',
                description:
                  'Session email approval: the PDF form rides as an attachment',
              })}
            </span>
          </div>
        ) : null
      }
      isBusy={isBusy}
      canApprove={parseAddressListInput(values.to).length > 0}
      approveLabel={intl.formatMessage({ defaultMessage: 'Send', id: '9WRlF4R2gm' })}
      pendingVerdict={pendingVerdict}
      onApprove={() =>
        submit(
          'approve',
          buildSessionEmailModifiedInput(approval.tool_input ?? {}, fields, values)
        )
      }
      onDeny={() => submit('deny')}
    />
  );
}
